import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import PageBanner from '../components/PageBanner';
import { FaHome, FaShieldAlt, FaUserSecret, FaLock, FaEye, FaDatabase, FaCog } from 'react-icons/fa';

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  return (
    <>
      <NavBar />
      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
            <Link to="/" className="flex items-center hover:text-green-600 transition-colors">
              <FaHome className="w-4 h-4 mr-1" />
              {t('privacy.home')}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">{t('privacy.breadcrumb')}</span>
          </nav>
        </div>

        <PageBanner />

        {/* Header Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('privacy.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('privacy.subtitle')}
            </p>
          </div>

          {/* Main Content */}
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaShieldAlt className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('privacy.commitmentTitle')}</h2>
              </div>
              <div className="space-y-6 text-gray-700">
                <p className="leading-relaxed">{t('privacy.commitmentText1')}</p>
                <p className="leading-relaxed">{t('privacy.commitmentText2')}</p>
              </div>
            </section>

            {/* Data Collection */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaDatabase className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('privacy.s1Title')}</h2>
              </div>
              <div className="space-y-6 text-gray-700">
                <h3 className="text-lg font-semibold text-gray-900">{t('privacy.s1aTitle')}</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {t('privacy.s1aItems', { returnObjects: true }).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>

                <h3 className="text-lg font-semibold text-gray-900">{t('privacy.s1bTitle')}</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {t('privacy.s1bItems', { returnObjects: true }).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Data Usage */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('privacy.s2Title')}</h2>
              <div className="space-y-6 text-gray-700">
                <h3 className="text-lg font-semibold text-gray-900">{t('privacy.s2aTitle')}</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {t('privacy.s2aItems', { returnObjects: true }).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>

                <h3 className="text-lg font-semibold text-gray-900">{t('privacy.s2bTitle')}</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {t('privacy.s2bItems', { returnObjects: true }).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Data Sharing */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaUserSecret className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('privacy.s3Title')}</h2>
              </div>
              <div className="space-y-6 text-gray-700">
                <h3 className="text-lg font-semibold text-gray-900">{t('privacy.s3aTitle')}</h3>
                <p className="leading-relaxed">{t('privacy.s3aText')}</p>

                <h3 className="text-lg font-semibold text-gray-900">{t('privacy.s3bTitle')}</h3>
                <p className="leading-relaxed">{t('privacy.s3bIntro')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {t('privacy.s3bItems', { returnObjects: true }).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>

                <h3 className="text-lg font-semibold text-gray-900">{t('privacy.s3cTitle')}</h3>
                <p className="leading-relaxed">{t('privacy.s3cIntro')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {t('privacy.s3cItems', { returnObjects: true }).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Data Security */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaLock className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('privacy.s4Title')}</h2>
              </div>
              <div className="space-y-6 text-gray-700">
                <p className="leading-relaxed">{t('privacy.s4Intro')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {t('privacy.s4Items', { returnObjects: true }).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="leading-relaxed">{t('privacy.s4Text')}</p>
              </div>
            </section>

            {/* Data Retention */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('privacy.s5Title')}</h2>
              <div className="space-y-6 text-gray-700">
                <p className="leading-relaxed">{t('privacy.s5Intro')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {t('privacy.s5Items', { returnObjects: true }).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="leading-relaxed">{t('privacy.s5Text')}</p>
              </div>
            </section>

            {/* User Rights */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaEye className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('privacy.s6Title')}</h2>
              </div>
              <div className="space-y-6 text-gray-700">
                <p className="leading-relaxed">{t('privacy.s6Intro')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>{t('privacy.s6ItemAccessLabel')}</strong> {t('privacy.s6ItemAccessText')}</li>
                  <li><strong>{t('privacy.s6ItemRectifyLabel')}</strong> {t('privacy.s6ItemRectifyText')}</li>
                  <li><strong>{t('privacy.s6ItemEraseLabel')}</strong> {t('privacy.s6ItemEraseText')}</li>
                  <li><strong>{t('privacy.s6ItemPortLabel')}</strong> {t('privacy.s6ItemPortText')}</li>
                  <li><strong>{t('privacy.s6ItemObjectLabel')}</strong> {t('privacy.s6ItemObjectText')}</li>
                  <li><strong>{t('privacy.s6ItemLimitLabel')}</strong> {t('privacy.s6ItemLimitText')}</li>
                </ul>
                <p className="leading-relaxed">
                  {t('privacy.s6Outro')}
                  <a href="mailto:contact@namke.app" className="text-[rgb(114,183,68)] hover:text-[rgb(90,150,55)] ml-1">
                    contact@namke.app
                  </a>
                </p>
              </div>
            </section>

            {/* Cookies */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('privacy.s7Title')}</h2>
              <div className="space-y-6 text-gray-700">
                <p className="leading-relaxed">{t('privacy.s7Intro')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {t('privacy.s7Items', { returnObjects: true }).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="leading-relaxed">{t('privacy.s7Text')}</p>
              </div>
            </section>

            {/* Updates */}
            <section className="bg-white rounded-2xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaCog className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('privacy.s8Title')}</h2>
              </div>
              <div className="space-y-6 text-gray-700">
                <p className="leading-relaxed">{t('privacy.s8Text1')}</p>
                <p className="leading-relaxed">{t('privacy.s8Text2')}</p>
                <p className="leading-relaxed">
                  <strong>{t('privacy.lastUpdatedLabel')}</strong> {t('privacy.lastUpdatedValue')}
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
